import type { TourIconId } from '../tourVisuals'
import type { User, UserPreferences } from '../../types/api'

export type TourId = string

export type TourStep = {
  id: string
  /** If omitted, stay on the current page (feature tours with dynamic URLs). */
  path?: string
  target: string
  titleKey: string
  bodyKey: string
  iconId: TourIconId
  /** Force tip above/below the spotlight (default: auto). */
  tipPlacement?: 'above' | 'bottom'
  /** Skip this step when the target is not in the DOM (role/state-dependent). */
  optional?: boolean
}

export type TourAutoStartContext = {
  user: User | null
  preferences: UserPreferences | null
}

/**
 * Named in-app tour. Register in `registry.ts`.
 * - `copyKey` maps to `t.tour[copyKey]` (welcome + steps).
 * - `version` bumps independently per tour id.
 * - `autoStart` runs once when prefs say progress < version.
 */
export type TourDefinition = {
  id: TourId
  version: number
  copyKey: string
  steps: TourStep[]
  /** Show welcome slides before coachmarks (coach onboarding). */
  hasWelcome?: boolean
  autoStart?: (ctx: TourAutoStartContext) => boolean
}

export function getTourProgress(
  preferences: UserPreferences | null | undefined,
  tourId: TourId
): number {
  if (!preferences) return 0
  const fromMap = preferences.tmaTourProgress?.[tourId]
  return typeof fromMap === 'number' ? fromMap : 0
}

export function isTourComplete(
  preferences: UserPreferences | null | undefined,
  tour: Pick<TourDefinition, 'id' | 'version'>
): boolean {
  return getTourProgress(preferences, tour.id) >= tour.version
}
